import { lawyerApi } from './http';

function toArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Upload a profile photo file to Supabase Storage via the backend.
 * @param {File} file  - The image File object from an <input type="file">
 * @returns {Promise<string>} - The public URL of the uploaded photo
 */
export async function uploadProfilePhoto(file) {
  const formData = new FormData();
  formData.append('photo', file);

  const { data } = await lawyerApi.post('/lawyer/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });

  if (!data?.photoUrl) throw new Error('No photo URL returned from server');
  return data.photoUrl;
}

/**
 * Delete the profile photo via the backend.
 * @returns {Promise<boolean>} - True if successful
 */
export async function deleteProfilePhoto() {
  const { data } = await lawyerApi.delete('/lawyer/photo');
  if (!data?.success) throw new Error(data?.message || 'Failed to remove photo');
  return true;
}

/**
 * Upload a verification document (Bar Council ID)
 * @param {File} file 
 * @returns {Promise<string>} - The public URL of the uploaded document
 */
export async function uploadVerificationDocument(file) {
  const formData = new FormData();
  formData.append('document', file);

  const { data } = await lawyerApi.post('/lawyer/document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });

  if (!data?.documentUrl) throw new Error('No document URL returned from server');
  return data.documentUrl;
}

export async function updateDashboardProfile(payload) {
  try {
    const { data } = await lawyerApi.patch('/lawyer/profile', payload);
    if (!data?.success) {
      throw new Error('Failed to update profile');
    }
    return data.profile || null;
  } catch (error) {
    const status = error?.response?.status;
    const message = String(error?.response?.data?.message || error?.message || '').toLowerCase();
    const isRouteMissing = status === 404 || message.includes('route patch') || message.includes('not found');

    if (!isRouteMissing) {
      throw error;
    }

    // Backward compatibility: older backend-lawyer versions only support POST /api/lawyer/profile.
    const { data: profileResponse } = await lawyerApi.get('/lawyer/profile');
    const existing = profileResponse?.profile || {};

    const state = payload?.state || existing?.state;
    const city = payload?.city || existing?.city;
    const languages = toArray(payload?.languages).length ? toArray(payload?.languages) : toArray(existing?.languages);
    const practiceAreas = toArray(payload?.practice_areas).length ? toArray(payload?.practice_areas) : toArray(existing?.practice_areas);
    const caseTypes = toArray(payload?.case_types).length ? toArray(payload?.case_types) : toArray(existing?.case_types);
    const courts = toArray(payload?.courts).length ? toArray(payload?.courts) : toArray(existing?.courts);
    const experience = payload?.experience ?? existing?.experience;

    if (!state || !city || !languages.length || !practiceAreas.length || !courts.length || !experience) {
      throw new Error('Profile setup incomplete. Please complete onboarding fields (state, city, language, courts, experience).');
    }

    const legacyPayload = {
      state,
      city,
      languages,
      practiceAreas,
      caseTypes,
      experience,
      courts,
      full_name: payload?.full_name,
      phone: payload?.phone,
      consultation_fee: payload?.consultation_fee,
    };

    const { data: legacyData } = await lawyerApi.post('/lawyer/profile', legacyPayload);
    if (!legacyData?.success) {
      throw new Error(legacyData?.message || 'Failed to update profile');
    }

    return {
      ...existing,
      full_name: payload?.full_name || existing?.full_name,
      email: payload?.email || existing?.email,
      phone: payload?.phone || existing?.phone,
      city,
      languages,
      practice_areas: practiceAreas,
      case_types: caseTypes,
      experience,
      consultation_fee: payload?.consultation_fee ?? existing?.consultation_fee,
      lawyerProfileId: existing?.lawyerProfileId || existing?.id || existing?.profile_id || '',
    };
  }
}

export async function fetchDashboardProfile() {
  const { data } = await lawyerApi.get('/lawyer/profile');
  if (!data?.success) {
    return null;
  }
  const profile = data.profile || null;
  if (!profile) return null;
  return {
    ...profile,
    lawyerProfileId: profile.lawyerProfileId || profile.id || profile.profile_id || '',
  };
}
